import React, { useEffect, useState } from 'react';
import {
  Input,
  InputGroup,
  Table,
  DOMHelper,
  Stack,
  Panel,
  IconButton,
  Pagination,
  Message,
  useToaster,
  Button,
  Tag
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import MoreIcon from '@rsuite/icons/legacy/More';
import { Edit, Icon } from '@rsuite/icons';
import { MdOutlineDeleteOutline, MdPersonAdd } from 'react-icons/md';
import DrawerView from './DrawerView';
import DrawerUpdate from './DrawerUpdate';
import ConfirmModal from '@/components/ConfirmModal';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import {
  getAccessProfiles,
  removeAccessProfile,
  getGroupsWithZonePriorities
} from '@/services/accessProfile.service';
import { fetchSetting } from '@/services/setting.service';
import AddProfileDrawer from './AddProfileDrawer';
import AddPriorityDrawer from './AddPriorityDrawer';
import UpdatePriorityDrawer from './EditDrawerTrue';
import { getZones } from '@/services/zone.service';
import { NameCell } from './Cells';
import { CLEAR_FILTERED_PRIORITY_ACCESS } from '@/Types/accessProfileTypes';
import { useTranslation } from 'react-i18next';
const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const toaster = useToaster();
  const dispatch = useAppDispatch();

  const [showDrawer, setShowDrawer] = useState(false);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [sortColumn, setSortColumn] = useState();
  const [sortType, setSortType] = useState();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [withPriority, setWithPriority] = useState(false);
  const [confirmModal, setConfirmModal] = useState<any>({
    open: false,
    title: '',
    subtitle: ''
  });
  const [showEditDrawer, setShowEditDrawer] = useState<any>({ open: false });
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const zonesStore = useAppSelector((state: any) => state.zone?.data);
  const accessProfiles = useAppSelector((state: any) => state.accessProfile.data);
  const loading = useAppSelector((state: any) => state.accessProfile.loading);
  const [filters, setFilters] = useState(
    withPriority ? { name: '', zone: '', priority: '', dayOfWeek: '' } : { name: '', zones: [] }
  );
  const filteredGroups = useAppSelector((state: any) => state.accessProfile.filteredGroups);

  useEffect(() => {
    const fetchPrioritySetting = async () => {
      try {
        const res: any = await fetchSetting('Homepage');
        setWithPriority(res?.configuration?.bookingWithPriority);
      } catch (e) {
        console.error('Error fetching priority setting:', e);
      }
    };
    fetchPrioritySetting();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (withPriority) {
        try {
          await dispatch(getGroupsWithZonePriorities());
        } catch (err) {
          console.error('Error fetching groups with priorities:', err);
        }
      } else {
        await dispatch(getAccessProfiles());
      }
    };

    fetchData();
  }, [dispatch, withPriority, filters]);

  useEffect(() => {
    const fetchZones = async () => {
      await dispatch(getZones());
    };
    fetchZones();
  }, [dispatch]);
  const { t } = useTranslation();

  useEffect(() => {
    setFilters(
      withPriority ? { name: '', zone: '', priority: '', dayOfWeek: '' } : { name: '', zones: [] }
    );
  }, [withPriority]);

  const handleSortColumn = (sortColumn, sortType) => {
    setSortColumn(sortColumn);
    setSortType(sortType);
  };

  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };
  const handleSearch = value => {
    setSearchKeyword(value);
    setPage(1);
  };

  useEffect(() => {
    if (withPriority) {
      dispatch(getGroupsWithZonePriorities());
    } else {
      dispatch(getAccessProfiles());
    }
  }, [dispatch, withPriority, filters]);

  const filteredData = () => {
    if (withPriority) {
      let filtered =
        Array.isArray(filteredGroups) && filteredGroups.length > 0 ? filteredGroups : [];

      if (searchKeyword) {
        filtered = filtered?.filter(item =>
          item.name?.toLowerCase().includes(searchKeyword.toLowerCase())
        );
      }

      if (filters.name) {
        filtered = filtered?.filter(item =>
          item.name?.toLowerCase().includes(filters.name.toLowerCase())
        );
      }

      if (filters.zone || filters.priority || filters.dayOfWeek !== '') {
        filtered = filtered?.filter(item => {
          if (!item.zonePriorities || item.zonePriorities.length === 0) {
            return false;
          }

          return item.zonePriorities.some(priority => {
            const zoneMatch =
              !filters.zone ||
              priority.zone?._id === filters.zone ||
              (priority.zone && priority.zone.toString() === filters.zone);

            const priorityMatch =
              !filters.priority || priority.priority === parseInt(filters.priority);

            const dayMatch =
              filters.dayOfWeek === '' || priority.dayOfWeek === parseInt(filters.dayOfWeek);

            return zoneMatch && priorityMatch && dayMatch;
          });
        });
      }

      if (sortColumn && sortType) {
        filtered = [...filtered].sort((a, b) => {
          let x = a[sortColumn] || '';
          let y = b[sortColumn] || '';

          if (typeof x === 'string') {
            x = x.toLowerCase();
            y = y.toLowerCase();
          }

          if (sortType === 'asc') {
            return x > y ? 1 : -1;
          } else {
            return x < y ? 1 : -1;
          }
        });
      }

      return filtered;
    }

    let filtered = accessProfiles;

    if (filters.name) {
      filtered = filtered?.filter(item =>
        item.name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    if (filters?.zones?.length > 0) {
      filtered = filtered?.filter(item => {
        const itemZoneIds = item.zones.map(zone => zone._id);
        return filters.zones.every(selectedZoneId => itemZoneIds.includes(selectedZoneId));
      });
    }

    if (searchKeyword) {
      const searchTerm = searchKeyword.toLowerCase();
      filtered = filtered?.filter(item => {
        const nameMatch = item.name.toLowerCase().includes(searchTerm);
        const descriptionMatch = item.description?.toLowerCase().includes(searchTerm);
        const zonesMatch = item.zones.some(zone => zone.name.toLowerCase().includes(searchTerm));
        return nameMatch || descriptionMatch || zonesMatch;
      });
    }

    if (sortColumn && sortType) {
      filtered = [...filtered].sort((a, b) => {
        let x = a[sortColumn];
        let y = b[sortColumn];

        if (typeof x === 'string') {
          x = x.toLowerCase();
          y = y.toLowerCase();
        }

        if (sortType === 'asc') {
          return x > y ? 1 : -1;
        } else {
          return x < y ? 1 : -1;
        }
      });
    }

    return filtered;
  };

  const handleDelete = async id => {
    try {
      await dispatch(removeAccessProfile(id));
      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t('Deleted successfully')}
        </Message>,
        { placement: 'topCenter' }
      );
    } catch (error) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(error.message)}
        </Message>,
        { placement: 'topCenter' }
      );
    }
    setConfirmModal({ open: false });
  };

  const handleFiltersChange = newFilters => {
    if (withPriority) {
      const cleanFilters = {
        name: newFilters.name || '',
        zone: newFilters.zone || '',
        priority: newFilters.priority || '',
        dayOfWeek: newFilters.dayOfWeek || ''
      };
      setFilters(cleanFilters);
    } else {
      setFilters(newFilters);
    }
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters(
      withPriority ? { name: '', zone: '', priority: '', dayOfWeek: '' } : { name: '', zones: [] }
    );
    setSearchKeyword('');
    setPage(1);

    if (withPriority) {
      dispatch({ type: CLEAR_FILTERED_PRIORITY_ACCESS });
      dispatch(getAccessProfiles());
    }
  };

  const renderPriorityTable = () => (
    <Table
      height={Math.max(getHeight(window) - 250, 400)}
      data={filteredData()}
      sortColumn={sortColumn}
      sortType={sortType}
      onSortColumn={handleSortColumn}
      loading={loading}
    >
      <Column width={500} sortable resizable>
        <HeaderCell>{t('Name')}</HeaderCell>
        <NameCell dataKey="name" placement="auto" />
      </Column>

      <Column width={500} sortable resizable>
        <HeaderCell>{t('Description')}</HeaderCell>
        <Cell dataKey="description" />
      </Column>

      <Column width={150} resizable>
        <HeaderCell>{t('Has Accesses')}</HeaderCell>
        <Cell>
          {rowData => (
            <Tag
              color={rowData.zonePriorities && rowData.zonePriorities.length > 0 ? 'green' : 'red'}
            >
              {rowData.zonePriorities && rowData.zonePriorities.length > 0 ? t('Yes') : t('No')}
            </Tag>
          )}
        </Cell>
      </Column>

      <Column width={100} flexGrow={0.5} fixed="right">
        <HeaderCell>
          <MoreIcon />
        </HeaderCell>
        <Cell style={{ padding: '6px' }}>
          {rowData => (
            <Stack>
              <IconButton
                onClick={() => {
                  setShowEditDrawer({
                    open: true,
                    data: rowData
                  });
                }}
                size="xs"
                icon={<Edit color="blue" />}
                id={`edit-${rowData._id}`}
              />
              <IconButton
                size="xs"
                icon={<MdOutlineDeleteOutline color="red" />}
                onClick={() => {
                  setConfirmModal({
                    open: true,
                    title: t('Are you sure to remove this access profile?'),
                    subtitle: t('You cannot undo this operation'),
                    onConfirm: () => {
                      handleDelete(rowData._id);
                    }
                  });
                }}
                id={`delete-${rowData._id}`}
              />
            </Stack>
          )}
        </Cell>
      </Column>
    </Table>
  );

  const renderNormalTable = () => (
    <Table
      height={Math.max(getHeight(window) - 250, 400)}
      data={filteredData()}
      sortColumn={sortColumn}
      sortType={sortType}
      onSortColumn={handleSortColumn}
      loading={loading}
    >
      <Column width={300} sortable resizable>
        <HeaderCell>{t('Name')}</HeaderCell>
        <Cell dataKey="name" />
      </Column>

      <Column width={230} sortable resizable>
        <HeaderCell>{t('Description')}</HeaderCell>
        <Cell dataKey="description" />
      </Column>

      <Column width={250} flexGrow={1} sortable resizable>
        <HeaderCell>Zones</HeaderCell>
        <Cell style={{ whiteSpace: 'pre-line' }}>
          {rowData => rowData.zones.map(item => `${item.name}, `).join('')}
        </Cell>
      </Column>

      <Column width={50} flexGrow={0.5} fixed="right">
        <HeaderCell>
          <MoreIcon />
        </HeaderCell>
        <Cell style={{ padding: '6px' }}>
          {rowData => (
            <Stack>
              <IconButton
                onClick={() => {
                  setShowEditDrawer({
                    open: true,
                    data: rowData
                  });
                }}
                size="xs"
                icon={<Edit color="blue" />}
                id={`edit-${rowData._id}`}
              />
              <IconButton
                size="xs"
                icon={<MdOutlineDeleteOutline color="red" />}
                onClick={() => {
                  setConfirmModal({
                    open: true,
                    title: t('Are you sure to remove this access profile?'),
                    subtitle: t('You cannot undo this operation'),
                    onConfirm: () => {
                      handleDelete(rowData._id);
                    }
                  });
                }}
                id={`delete-${rowData._id}`}
              />
            </Stack>
          )}
        </Cell>
      </Column>
    </Table>
  );

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t('Search')} value={searchKeyword} onChange={handleSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
        </Stack>
        <Stack spacing={6}>
          <IconButton
            appearance="primary"
            icon={<Icon as={MdPersonAdd} />}
            onClick={() => setShowAddDrawer(true)}
          >
            {t('Add Access Profile')}
          </IconButton>

          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            {t('Filters')}
          </Button>
        </Stack>
      </Stack>
      <Panel bodyFill>
        {withPriority ? renderPriorityTable() : renderNormalTable()}
        <div style={{ padding: 20 }}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={accessProfiles?.length || 0}
            limitOptions={[10, 30, 50]}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      </Panel>
      {withPriority ? (
        <AddPriorityDrawer open={showAddDrawer} onClose={() => setShowAddDrawer(false)} />
      ) : (
        <AddProfileDrawer open={showAddDrawer} onClose={() => setShowAddDrawer(false)} />
      )}
      <DrawerView
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        zones={zonesStore}
        withPriority={withPriority}
        toaster={toaster}
      />
      {withPriority ? (
        <UpdatePriorityDrawer
          open={showEditDrawer.open}
          onClose={() => setShowEditDrawer({ open: false })}
          data={showEditDrawer.data}
        />
      ) : (
        showEditDrawer.open && (
          <DrawerUpdate
            open={showEditDrawer.open}
            onClose={() => setShowEditDrawer({ open: false })}
            data={showEditDrawer.data}
          />
        )
      )}
      {confirmModal.open && (
        <ConfirmModal confirmDialog={confirmModal} setConfirmDialog={setConfirmModal} />
      )}
    </>
  );
};

export default DataTable;
